import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { CalendarDays, FileText, Receipt, Sparkles } from "lucide-react";
import { getAdminKpis, getMyRole } from "@/lib/dr-sara.functions";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

const kpiQuery = () =>
  queryOptions({
    queryKey: ["admin", "kpis"],
    queryFn: () => getAdminKpis(),
  });

export const Route = createFileRoute("/_authenticated/admin/")({
  loader: async ({ context }) => {
    const role = await context.queryClient.ensureQueryData({
      queryKey: ["me", "role"],
      queryFn: () => getMyRole(),
    });
    if (!role.isStaff) {
      throw redirect({ to: "/portal", search: {} });
    }
    await context.queryClient.ensureQueryData(kpiQuery());
  },
  notFoundComponent: RouteNotFound,
  component: AdminHome,
  errorComponent: ({ error }) => (
    <div className="rounded-ds-lg bg-card p-8 text-center shadow-soft">
      <p className="text-muted-foreground">تعذّر تحميل اللوحة الآن. جرّب التحديث.</p>
      <p className="mt-2 text-xs text-muted-foreground/70">{String(error)}</p>
    </div>
  ),
});

function AdminHome() {
  const { data } = useSuspenseQuery(kpiQuery());

  const isAdmin = Boolean((data as { isAdmin?: boolean }).isAdmin);

  const cards = [
    {
      label: "حجوزات بانتظار التأكيد",
      value: data.pendingBookings,
      icon: CalendarDays,
      tint: "bg-primary/25",
      to: "/admin/bookings",
      note: null as string | null,
    },
    {
      label: "مدفوعات بانتظار المراجعة",
      value: data.pendingReceipts,
      icon: Receipt,
      tint: "bg-soft-blue/25",
      to: "/admin/pending-purchases",
      note: null as string | null,
    },
    ...(isAdmin
      ? [
          {
            label: "طلبات البوصلة الذكية بانتظار إجراء",
            value: data.smartNeedsAction,
            icon: Sparkles,
            tint: "bg-modern-orange/20",
            to: "/admin/bousala/smart",
            note: data.smartOverdue > 0 ? `${data.smartOverdue} متأخّرة عن الموعد` : null,
          },
          {
            label: "مقالات منشورة",
            value: data.publishedPosts,
            icon: FileText,
            tint: "bg-soft-green/25",
            to: "/admin/blog",
            note: null as string | null,
          },
        ]
      : []),
  ];

  return (
    <div>
      <header className="mb-8">
        <p className="kicker text-modern-orange">لوحة التحكم</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold text-foreground">
          نظرة سريعة على اليوم
        </h1>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Link
              key={c.label}
              to={c.to as any}
              className="rounded-ds-lg bg-card p-5 shadow-soft transition-shadow hover:shadow-lift"
            >
              <div className="flex items-center justify-between">
                <span className={`grid h-10 w-10 place-items-center rounded-2xl ${c.tint} text-foreground`}>
                  <Icon className="h-5 w-5" />
                </span>
              </div>
              <p className="mt-4 font-display text-3xl font-extrabold text-foreground">{c.value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{c.label}</p>
              {c.note ? (
                <p className="mt-2 text-xs font-bold text-modern-orange">{c.note}</p>
              ) : null}
            </Link>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="rounded-ds-lg bg-card p-6 shadow-soft">
          <h2 className="font-display text-lg font-bold text-foreground">آخر الحجوزات</h2>
          {data.recentBookings.length === 0 ? (
            <p className="mt-4 rounded-2xl border border-dashed border-border bg-background/50 p-6 text-center text-sm text-muted-foreground">
              لا يوجد حجوزات بعد.
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.recentBookings.map((b: any) => (
                <li key={b.id} className="flex items-center justify-between rounded-2xl bg-background/60 px-4 py-3 text-sm">
                  <span className="font-semibold text-foreground">{statusAr(b.status)}</span>
                  <span className="text-muted-foreground">
                    {new Date(b.created_at).toLocaleDateString("ar-u-nu-latn")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-ds-lg bg-card p-6 shadow-soft">
          <h2 className="font-display text-lg font-bold text-foreground">الأكثر قراءةً</h2>
          {data.popularPosts.length === 0 ? (
            <p className="mt-4 rounded-2xl border border-dashed border-border bg-background/50 p-6 text-center text-sm text-muted-foreground">
              لا يوجد مقالات منشورة بعد.
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.popularPosts.map((p: any) => (
                <li key={p.id} className="flex items-center justify-between rounded-2xl bg-background/60 px-4 py-3 text-sm">
                  <span className="font-semibold text-foreground">{p.title_ar}</span>
                  <span className="text-muted-foreground">{p.views_count} قراءة</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function statusAr(s: string) {
  const map: Record<string, string> = {
    new: "جديدة",
    contacted: "تم التواصل",
    awaiting_payment: "بانتظار الدفع",
    confirmed: "مؤكدة",
    completed: "مكتملة",
    cancelled: "ملغاة",
    no_show: "لم يحضر",
  };
  return map[s] ?? s;
}